import type { ReactNode } from 'react';
import type { TooltipPlacement } from '../Tooltip/Tooltip';

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import Tooltip from '../Tooltip/Tooltip';

interface TabProps {
  label: ReactNode;
  key: string;
  render: () => ReactNode;
  disabled?: boolean;
  tooltip?: string;
  tooltipPlacement?: TooltipPlacement;
  phoneTooltipPlacement?: TooltipPlacement;
}

interface TabsProps {
  /**
   * Tabs content
   */
  tabs: TabProps[];
  /**
   * Index of default selected tab (starts from 0)
   */
  defaultSelectedIndex?: number;
  /**
   * Callback run on tab change
   */
  onChange?: (tab: number) => void;
  /**
   * If selecting new tab on onChange should be ignored
   */
  ignoreSelecting?: boolean;
  /**
   * Tabs container additional CSS classes
   */
  additionalContainerClasses?: string;
  /**
   * Single tab additional CSS classes
   */
  additionalTabClasses?: string;
  /**
   * Tabs panels container additional CSS classes
   */
  additionalTabPanelsClasses?: string;
  /**
   * If the active tab panel should fill available height and scroll
   * internally, instead of growing naturally with page scroll.
   */
  fillHeight?: boolean;
  /**
   * Test id for tabs component
   */
  testId?: string;
}

const Tabs = ({
  tabs,
  defaultSelectedIndex = 0,
  onChange,
  ignoreSelecting = false,
  additionalContainerClasses = '',
  additionalTabClasses = '',
  additionalTabPanelsClasses = '',
  fillHeight = false,
  testId = '',
}: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState(defaultSelectedIndex);

  useEffect(() => {
    setSelectedTab(defaultSelectedIndex);
  }, [defaultSelectedIndex]);

  const handleChange = useCallback(
    (tab: number) => {
      if (!ignoreSelecting) setSelectedTab(tab);
      if (onChange) {
        onChange(tab);
      }
    },
    [ignoreSelecting, onChange],
  );

  return (
    <div
      className={twMerge(
        'flex flex-col',
        fillHeight ? 'h-full md:min-h-0' : 'h-full',
        additionalContainerClasses,
      )}
      {...getTestProps(testId, 'tabs-container')}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <TabGroup
        selectedIndex={selectedTab}
        onChange={handleChange}
        manual
        {...(fillHeight
          ? { className: 'flex flex-col md:h-full md:min-h-0' }
          : {})}
      >
        <TabList
          className={twMerge(
            'border-b dark:border-slate-800 flex flex-wrap scrollbar-sm scrollbar-hidden',
            fillHeight && 'md:shrink-0',
          )}
        >
          {tabs.map((tab, idx) => (
            <Tooltip
              key={`${tab.key}-tab-selector`}
              tooltip={tab.tooltip}
              tooltipPlacement={tab.tooltipPlacement}
              phoneTooltipPlacement={
                tab.phoneTooltipPlacement || tab.tooltipPlacement
              }
            >
              <Tab
                className={twMerge(
                  'font-semibold uppercase outline-0',
                  'mx-1 px-1 py-2 md:mx-2 md:px-7 md:py-4',
                  'text-sm md:text-base whitespace-nowrap',
                  'border-b-[3px] border-transparent -mb-0.5 dark:text-slate-200',
                  'disabled:text-slate-400 dark:disabled:text-slate-600',
                  'data-selected:border-blue data-selected:text-blue dark:data-selected:text-blue',
                  additionalTabClasses,
                )}
                disabled={tab.disabled}
                {...getTestProps(testId, `${idx}-tab-selector`)}
              >
                {tab.label}
              </Tab>
            </Tooltip>
          ))}
        </TabList>
        <TabPanels
          className={twMerge(
            additionalTabPanelsClasses,
            'mt-5 md:mt-3',
            fillHeight ? 'md:flex-1 md:min-h-0 md:overflow-hidden' : 'h-full',
          )}
        >
          {tabs.map((tab, idx) => (
            <TabPanel
              key={`${tab.key}-tab-panel`}
              className={twMerge(
                'h-full',
                fillHeight && 'md:flex md:flex-col md:min-h-0',
              )}
              {...getTestProps(testId, `${idx}-tab-panel`)}
              tabIndex={-1}
            >
              {tab.render()}
            </TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </div>
  );
};

export default Tabs;
